import * as React from "react"
import { Button, Icon, Popover, PopoverContent, PopoverTrigger, cn } from "@nexus/ui"
import { useTranslation } from "react-i18next"
import { adPlatformLabel } from "@/lib/ad-platform-labels"
import { AdPlatformIcon } from "../../AdPlatformIcon"
import type { BrandVoice, StrategicPriorities } from "../../../types/opportunities.types"
import { humanizeBrandName } from "../../../lib/format-opportunity.js"
import { normalizeAdPlatforms } from "../../../lib/format-opportunity.js"
import { describeSetDateWindow } from "../../../lib/set-date-window-display.js"
import {
  formatDurationMs,
  formatFullLocalDateTime,
  getGenerationRunDurationMs,
} from "../../../lib/format-relative-time.js"

const SET_PARAMS_POPOVER_COLLISION_PADDING = 8

/** Fields the popover reads. `OpportunitySet` satisfies this, so the old page keeps its prop. */
export interface SetParams {
  industryCompetitors: string | string[] | null
  myBrand: string | null
  mainCompetitor: string | null
  products: string[] | null
  adPlatforms: string[] | string | null
  minimalGeneration: boolean
  strategicPriorities: StrategicPriorities | null
  brandVoice: BrandVoice | null
  startDate?: string | null
  endDate?: string | null
  generationStartedAt: string | null
  generationCompletedAt: string | null
  createdAt: string
}

export interface SetParamsPopoverProps {
  set: SetParams
  /** Human-readable My Industry label (e.g. "Ingrown Hair") — derived by caller from industryLabelByIndex. */
  industryLabel?: string | null
  /** Human-readable Benchmark Industry label. */
  benchmarkIndustryLabel?: string | null
  /** Persona titles. Omitted on the old Opportunities page. */
  personas?: string[]
  /** When true, button is always visible (set view header). When false (default), fades in on card hover. */
  alwaysVisible?: boolean
  /** Live duration tick while generation is in progress. */
  isGenerating?: boolean
  className?: string
}

function competitorNames(value: SetParams["industryCompetitors"]): string[] {
  if (!value) return []
  const parts = typeof value === "string" ? value.split(",") : value
  return parts.map((name) => name.trim()).filter(Boolean)
}

function useTickingNow(active: boolean): number {
  const [now, setNow] = React.useState(() => Date.now())
  React.useEffect(() => {
    if (!active) return
    setNow(Date.now())
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [active])
  return now
}

export function SetParamsPopover({
  set,
  industryLabel,
  benchmarkIndustryLabel,
  personas = [],
  alwaysVisible = false,
  isGenerating = false,
  className,
}: SetParamsPopoverProps) {
  const { t } = useTranslation()

  const competitors = React.useMemo(
    () => competitorNames(set.industryCompetitors),
    [set.industryCompetitors],
  )
  const personaTitles = personas.map((title) => title.trim()).filter(Boolean)

  const adPlatforms = normalizeAdPlatforms(set.adPlatforms)
  const focusOn = set.strategicPriorities?.focusOn?.filter(Boolean) ?? []
  const avoid = set.strategicPriorities?.avoid?.filter(Boolean) ?? []
  const alwaysSay = set.brandVoice?.alwaysSay?.trim() ?? ""
  const neverSay = set.brandVoice?.neverSay?.trim() ?? ""

  const myIndustryLabel = industryLabel?.trim() ? industryLabel : null
  const benchmarkLabel = benchmarkIndustryLabel?.trim() ? benchmarkIndustryLabel : null
  const hasIndustries = !!(myIndustryLabel || benchmarkLabel)
  const hasBasic = !!(hasIndustries || personaTitles.length || set.myBrand || set.mainCompetitor || competitors.length || set.products?.length || adPlatforms.length)
  const hasAdvanced = !!(focusOn.length || avoid.length || alwaysSay || neverSay)
  const hasMinimal = set.minimalGeneration === true
  const dateWindow = describeSetDateWindow(set.startDate, set.endDate, t)

  const generationStartedIso = set.generationStartedAt ?? set.createdAt
  const startedAtLabel = formatFullLocalDateTime(generationStartedIso)
  const tickingNow = useTickingNow(isGenerating)
  const runDurationMs = getGenerationRunDurationMs(
    generationStartedIso,
    set.generationCompletedAt,
    isGenerating,
    isGenerating ? tickingNow : Date.now(),
  )

  return (
    <Popover>
      <PopoverTrigger
        asChild
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") e.stopPropagation()
        }}
      >
        <Button
          variant="ghost"
          size="icon-sm"
          className={cn(
            "text-muted-foreground transition-[opacity,color] hover:text-primary",
            "min-h-11 min-w-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-7 [@media(hover:hover)_and_(pointer:fine)]:min-w-7",
            !alwaysVisible &&
              "opacity-0 group-hover/card:opacity-100 group-focus-visible/card:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 [@media(hover:none)]:opacity-100",
            className,
          )}
          aria-label={t("opportunities.setCard.paramsAria")}
        >
          <Icon name="info" size="sm" color="inherit" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={4}
        collisionPadding={SET_PARAMS_POPOVER_COLLISION_PADDING}
        className="w-72 overflow-hidden p-0"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-border/50 px-3.5 py-2.5">
          <Icon name="settings" size="xs" className="shrink-0 text-primary/60" />
          <span className="text-xs font-semibold text-foreground">
            {t("opportunities.setCard.paramsTitle")}
          </span>
        </div>

        <div className="max-h-[min(var(--radix-popover-content-available-height),420px)] overflow-y-auto px-3.5 py-3">
          <div className="flex flex-col gap-3">

            {startedAtLabel && (
              <ParamSection label={t("opportunities.setCard.paramsStartedAt")}>
                <p className="text-[11px] leading-relaxed text-foreground/75">{startedAtLabel}</p>
              </ParamSection>
            )}

            {runDurationMs != null && (
              <ParamSection label={t("opportunities.setCard.paramsRunDuration")}>
                <p
                  className="tabular-nums text-[11px] leading-relaxed text-foreground/75"
                  {...(isGenerating ? { role: "timer" as const, "aria-live": "off" as const } : {})}
                >
                  {formatDurationMs(runDurationMs)}
                </p>
              </ParamSection>
            )}

            {dateWindow && (
              <ParamSection label={t("opportunities.setCard.paramsDateRange", "Date range")}>
                <div className="flex flex-col gap-1">
                  <Chip>{dateWindow.presetLabel}</Chip>
                  <p className="text-[11px] leading-relaxed text-foreground/75">{dateWindow.rangeLabel}</p>
                </div>
              </ParamSection>
            )}

            {(startedAtLabel || runDurationMs != null || dateWindow) && (hasBasic || hasMinimal || hasAdvanced) && (
              <div className="-mx-3.5 border-t border-border/40" />
            )}

            {hasIndustries && (
              <div className={myIndustryLabel && benchmarkLabel ? "grid grid-cols-2 gap-2" : "flex flex-col gap-2"}>
                {myIndustryLabel && (
                  <ParamSection label={t("opportunities.industryLabel")}>
                    <Chip variant="brand">{myIndustryLabel}</Chip>
                  </ParamSection>
                )}
                {benchmarkLabel && (
                  <ParamSection label={t("opportunities.benchmarkIndustryLabel")}>
                    <Chip variant="benchmark">{benchmarkLabel}</Chip>
                  </ParamSection>
                )}
              </div>
            )}

            {personaTitles.length > 0 && (
              <ParamSection label={t("opportunities.setCard.paramsPersonas", "Personas")}>
                <ChipList chips={personaTitles} />
              </ParamSection>
            )}

            {(set.myBrand || set.mainCompetitor) && (
              <div className="grid grid-cols-2 gap-2">
                {set.myBrand && (
                  <ParamSection label={t("opportunities.setCard.paramsMyBrand")}>
                    <Chip variant="brand">{humanizeBrandName(set.myBrand)}</Chip>
                  </ParamSection>
                )}
                {set.mainCompetitor && (
                  <ParamSection label={t("opportunities.setCard.paramsMainCompetitor")}>
                    <Chip variant="benchmark">{humanizeBrandName(set.mainCompetitor)}</Chip>
                  </ParamSection>
                )}
              </div>
            )}

            {competitors.length > 0 && (
              <ParamSection label={t("opportunities.setCard.paramsCompetitors")}>
                <ChipList chips={competitors.map(humanizeBrandName)} />
              </ParamSection>
            )}

            {set.products?.length ? (
              <ParamSection label={t("opportunities.setCard.productsLabel")}>
                <ChipList chips={set.products} />
              </ParamSection>
            ) : null}

            {adPlatforms.length > 0 && (
              <ParamSection label={t("opportunities.setCard.adPlatformsLabel")}>
                {adPlatforms.map((p) => (
                  <Chip key={p}>
                    <AdPlatformIcon platformId={p.toLowerCase()} className="size-3" />
                    {adPlatformLabel(t, p, humanizeBrandName(p))}
                  </Chip>
                ))}
              </ParamSection>
            )}

            {hasMinimal && (
              <ParamSection label={t("opportunities.setCard.paramsMinimalMode")}>
                <Chip variant="warning">{t("opportunities.setCard.paramsMinimalGeneration")}</Chip>
              </ParamSection>
            )}

            {/* only rendered when both sections coexist */}
            {(hasBasic || hasMinimal) && hasAdvanced && (
              <div className="-mx-3.5 border-t border-border/40" />
            )}

            {(focusOn.length > 0 || avoid.length > 0) && (
              <ParamSection label={t("opportunities.setCard.paramsStrategicPriorities")}>
                <div className="flex flex-col gap-2">
                  {focusOn.length > 0 && (
                    <div>
                      <span className="mb-1 block text-[10px] text-muted-foreground/60">
                        {t("opportunities.setCard.paramsFocusOn")}
                      </span>
                      <ChipList chips={focusOn} variant="success" />
                    </div>
                  )}
                  {avoid.length > 0 && (
                    <div>
                      <span className="mb-1 block text-[10px] text-muted-foreground/60">
                        {t("opportunities.setCard.paramsAvoid")}
                      </span>
                      <ChipList chips={avoid} variant="destructive" />
                    </div>
                  )}
                </div>
              </ParamSection>
            )}

            {(alwaysSay || neverSay) && (
              <ParamSection label={t("opportunities.setCard.paramsBrandVoice")}>
                <div className="flex flex-col gap-2">
                  {alwaysSay && (
                    <div>
                      <span className="mb-0.5 block text-[10px] text-muted-foreground/60">
                        {t("opportunities.setCard.paramsAlwaysSay")}
                      </span>
                      <p className="text-[11px] leading-relaxed text-foreground/75">{alwaysSay}</p>
                    </div>
                  )}
                  {neverSay && (
                    <div>
                      <span className="mb-0.5 block text-[10px] text-muted-foreground/60">
                        {t("opportunities.setCard.paramsNeverSay")}
                      </span>
                      <p className="text-[11px] leading-relaxed text-foreground/75">{neverSay}</p>
                    </div>
                  )}
                </div>
              </ParamSection>
            )}

          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

// ─── Helpers ─────────────────────────────────────────────────────────

function ParamSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/45">
        {label}
      </span>
      {children}
    </div>
  )
}

type ChipVariant = "default" | "brand" | "benchmark" | "success" | "destructive" | "warning"

const CHIP_STYLES: Record<ChipVariant, string> = {
  default: "bg-muted/70 text-muted-foreground/80",
  brand: "bg-primary/10 text-primary font-semibold",
  benchmark: "bg-violet-subtle text-violet-subtle-foreground font-semibold",
  success: "bg-status-success/10 text-status-success/80",
  destructive: "bg-destructive/10 text-destructive/70",
  warning: "bg-warning-subtle text-warning-subtle-foreground",
}

function Chip({ children, variant = "default" }: { children: React.ReactNode; variant?: ChipVariant }) {
  return (
    <span
      className={cn(
        "inline-flex max-w-full items-center gap-1 truncate rounded-md px-1.5 py-0.5 text-[11px] font-medium leading-tight",
        CHIP_STYLES[variant],
      )}
    >
      {children}
    </span>
  )
}

function ChipList({ chips, variant = "default" }: { chips: string[]; variant?: ChipVariant }) {
  return (
    <div className="flex flex-wrap gap-1">
      {chips.map((chip) => (
        <Chip key={chip} variant={variant}>
          {chip}
        </Chip>
      ))}
    </div>
  )
}
